import { useAtomValue, useSetAtom } from 'jotai';
import { formatDistance } from 'date-fns';
import { Card, CardMedia, Stack, Typography, useTheme } from '@mui/material';

import { selectedDeviceAtom } from '../common/MapView';
import { tempUnitAtom } from '@/interfaces/utils';
import { getTemperatureText } from '@/components/common/utils';

import type { Device } from '@/@types/device';

interface DeviceCardProps {
  device: Device;
}

const PADDING_Y = 2;
const WARNING_BORDER_WIDTH = 7;

export default function DeviceCard({ device }: DeviceCardProps) {
  const tempUnit = useAtomValue(tempUnitAtom);
  const setSelectedDevice = useSetAtom(selectedDeviceAtom);
  const theme = useTheme();
  const SCALING_FACTOR = Number(theme.spacing(1).split('px')[0]);

  const lastTempTimeRelative = device.data.time
    ? formatDistance(device.data.time, new Date(), { addSuffix: true })
    : '';
  let borderTop = '';
  let pt: string | number = PADDING_Y;
  let tempColor = '';

  if (device.status === 'alarm') {
    borderTop = `${WARNING_BORDER_WIDTH}px solid red`;
    pt = `${PADDING_Y * SCALING_FACTOR - WARNING_BORDER_WIDTH}px`;
    tempColor = 'red';
  }

  function onClick() {
    setSelectedDevice(device);
  }

  return (
    <Card
      onClick={onClick}
      sx={{
        display: 'flex',
        minWidth: 340,
        minHeight: 90,
        cursor: 'pointer',
        boxShadow: 4,
        borderRadius: '12px',
        borderTop,
        my: 2,
        mx: 1,
        pt,
        pb: PADDING_Y,
        px: 3,
      }}
    >
      <CardMedia
        component="img"
        src="/images/devices/sensor.png"
        sx={{ objectFit: 'contain', width: 48, mt: -0.5, mr: 1 }}
      />
      <Stack justifyContent="space-between" sx={{ flexGrow: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={500}>{device.name}</Typography>
          <Stack alignItems="flex-end">
            <Typography fontSize={20} fontWeight="bold" color={tempColor}>
              {device.data.temperature ? getTemperatureText(device.data.temperature, tempUnit, true) : '\u00A0'}
            </Typography>
            {device.isHumiditySupported && device.data.humid && (
              <Typography fontSize={20} fontWeight="bold" color={tempColor} sx={{ mt: -1 }}>
                {`${device.data.humid}%`}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography fontSize={12} color="#5490A1">
            {device.serialNumber}
          </Typography>
          <Typography fontSize={14} sx={{ opacity: 0.5 }}>
            {lastTempTimeRelative}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
}
